import {Injectable, Input, TemplateRef} from '@angular/core';
import {
  ApDynformsConfigFieldset
}                                       from '../../../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {PpLogfileEditWizardUiProvider}                     from './pp-logfile-edit-wizard.ui-provider';
import {BehaviorSubject, Observable, of, Subscription}     from 'rxjs';
import {
  ApDynGridPagerConfigBase
}                                                          from '../../../../ap-dyngrids/config/ap-dyn-grid-pager-config-base';
import {ILogFileEditWizard, IPpLogEditProductListGridItem} from '../pp-logfile-edit-wizard.types';
import {
  PlantProtectionPlanBookStore
}                                                          from '../../../../stores/plant-protection/plant-protection-planbook.store';
import {distinctUntilChanged, map}                         from 'rxjs/operators';
import {TranslationStore}                                  from '../../../../stores/translation/translation.store';
import {
  PpCommonService
}                                                          from '../../../../plant-protection/services/pp-common.service';
import {
  ApDynGridColumnConfigBase
}                                                          from '../../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {ApDynformsComponent}                               from '../../../../ap-dynforms/ap-dynforms.component';
import {IDisplayName}                                      from '../../../../types/common-types';
import {
  PlantProtectionProductStore
}                                                          from '../../../../stores/base-data/plantprotection.product.store';
import {CropTypeStore}                                     from '../../../../stores/base-data/crop.types.store';
import {ApTranslationService}                              from '../../../../ap-utils/service/ap-translation.service';
import {UnitsStore}                                        from '../../../../stores/common/units.store';
import {nameOf}                                            from '../../../../ap-core/utils/ap-name-of';
import IDatasetFieldMap = Data.Sensor.IDatasetFieldMap;
import IPlantProtectionProduct = Data.BaseData.IPlantProtectionProduct;
import IPlantProtectionPlanBook = Data.TaskManagement.IPlantProtectionPlanBook;
import IPlantProtectionSensorLogHeaderData = Data.Import.IPlantProtectionSensorLogHeaderData;

@Injectable({providedIn: 'root'})
export class PpLogfileEditWizardProvider {
  public fieldSets: ApDynformsConfigFieldset[];
  public loading$: Observable<boolean> = of(false);
  public pager: ApDynGridPagerConfigBase;
  @Input() public data: IDatasetFieldMap;
  public dynForm: ApDynformsComponent;
  public productColumns: ApDynGridColumnConfigBase[];
  public productList$ = new BehaviorSubject<IPpLogEditProductListGridItem[]>([]);

  private _subscriptions: Subscription[] = [];

  constructor(private commonService: PpCommonService,
              private cropTypeStore: CropTypeStore,
              private plantProtectionStore: PlantProtectionPlanBookStore,
              private productStore: PlantProtectionProductStore,
              private translationService: ApTranslationService,
              private translationStore: TranslationStore,
              private uiProvider: PpLogfileEditWizardUiProvider,
              private unitStore: UnitsStore) {
    this.productColumns = this.uiProvider.getProductList();
  }

  public onInit(templateRef: TemplateRef<any>): void {
    this._buildFieldSets(templateRef);
    this._setProducts();
    this._subscriptions.push(this.uiProvider.addProduct.subscribe(() => this._addProduct()));
    this._subscriptions.push(this.uiProvider.deleteProduct.subscribe((value) => this._deleteProduct(value)));
  }

  public onDestroy(): void {
    this._subscriptions.forEach((s) => s.unsubscribe());
  }

  private _buildFieldSets(templateRef: TemplateRef<any>): void {
    this.fieldSets = [
      this.uiProvider.getLogfileFieldset(this.data),
      this.uiProvider.getProductsFieldset(this.data, this.dynForm.formValues$, this._getPlannings(), this.productList$, templateRef),
      this.uiProvider.getDocuFieldset(this.data, this.dynForm.formValues$),
    ];
  }

  private _getPlannings(): Observable<IDisplayName[]> {
    const headerValues: IPlantProtectionSensorLogHeaderData = JSON.parse(this.data.DataSet.HeaderValuesString);
    return this.plantProtectionStore.Listen((s) => s.data)
      .pipe(map((planBooks) => {
        const resultList = [];
        resultList.push({
          Id: null,
          DisplayName: `(${this.translationStore.FindTranslationForSelectedLanguage('Global__New_Planning')})`
        });
        resultList.push(...planBooks
          .filter((planBook) => planBook.FieldGeomId === this.data.FieldGeomId &&
            planBook.LogfileOperationMode === +headerValues.OperationMode)
          .map((planBook) => {
            return {
              Id: planBook.Id as string,
              DisplayName: this.commonService.getPlanningDescription(planBook)
            };
          }));
        return resultList;
      }));
  }

  private _addProduct(): void {
    const dataItem = this.dynForm.form.getRawValue() as ILogFileEditWizard;
    const productItem = this.productStore.getProducts().find((p) => p.Id === dataItem.Product);
    const unit = this._getUnit(productItem);
    const newItem: IPpLogEditProductListGridItem = {
      ProductItem: productItem,
      ApplRate: dataItem.AvgProduct,
      Unit: unit,
      ButtonIconName: 'trashIcon'
    };
    this.productList$.next([...this.productList$.value, newItem]);
    this.dynForm.form.get(nameOf<ILogFileEditWizard>('Product')).setValue(undefined);
  }

  private _deleteProduct(dataItem: IPpLogEditProductListGridItem): void {
    this.productList$.next(this.productList$.value.filter((p) => p.ProductItem.Id !== dataItem.ProductItem.Id));
  }

  private _getUnit(product: IPlantProtectionProduct): string {
    const details = product?.ProductDetails.find((d) =>
      d.EcMin <= this.data.Ec && d.EcMax >= this.data.Ec && this.cropTypeStore.GetEppoCode(this.data.CropType) === d.EppoCrop);
    return details?.Unit?.Key;
  }

  private _setProducts(): void {
    setTimeout(() => {
      this._subscriptions.push(this.dynForm.form.valueChanges.pipe(
        distinctUntilChanged((previous: ILogFileEditWizard, current: ILogFileEditWizard) => previous.Plan === current.Plan),
        map((data: ILogFileEditWizard) =>
          this.plantProtectionStore.Listen((s) => s.data)
            .value.find((p) => p.Id === data.Plan)),
      ).subscribe((planning: IPlantProtectionPlanBook) => {
        this._setProduct(planning);
      }));
      const values: ILogFileEditWizard = this.dynForm.form.value;
      this._setProduct(this.plantProtectionStore.Listen((s) => s.data)
        .value.find((p) => p.Id === values.Plan));
    });
  }

  private _setProduct(planning: IPlantProtectionPlanBook): void {
    if (planning === undefined) {
      const json: IPlantProtectionSensorLogHeaderData = JSON.parse(this.data.DataSet.HeaderValuesString);
      if (json?.Agents) {
        this.productList$.next(json?.Agents
          .map((product) => ({
            Product: product,
            ProductItem: this.productStore.Listen((s) => s.data)
              .value.find((value) => value.ExternalProductId === product.Id)
          }))
          .filter((item) => item.ProductItem != null)
          .map((item): IPpLogEditProductListGridItem => {
            return {
              ProductItem: item.ProductItem,
              ApplRate: +item.Product.AStd.replace(',', '.'),
              Unit: this.unitStore.getUnit(item.Product.Unit)?.Key ?? item.Product.Unit,
              ButtonIconName: 'trashIcon'
            };
          }) ?? []);
      }
    } else {
      this.productList$.next(planning?.ProductList.map((product): IPpLogEditProductListGridItem => {
        const unit = this.unitStore.getUnitWithUnit(product.Unit.Id).Key;
        return {
          ProductItem: product.Product,
          ApplRate: product.ProductRate,
          Unit: unit,
          ButtonIconName: 'trashIcon'
        };
      }) ?? []);
    }
  }
}
